import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, User, Lock, LogIn, AlertCircle, Globe } from 'lucide-react';
import apiService from '../services/api';

const Login = ({ onLogin }) => {
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [appSettings, setAppSettings] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'tr');
  const [versionInfo, setVersionInfo] = useState(null);

  // Load app settings and apply default language
  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiService.request('/chat/app-settings');
        setAppSettings(data || {});

        const userLang = localStorage.getItem('chatbot_language');
        const defaultLang = data?.defaultLanguage;
        const lang = userLang || defaultLang || 'tr';
        if (lang !== i18n.language) {
          i18n.changeLanguage(lang);
          setCurrentLanguage(lang);
        }
      } catch (err) {
        const lang = localStorage.getItem('chatbot_language') || 'tr';
        if (lang !== i18n.language) {
          i18n.changeLanguage(lang);
          setCurrentLanguage(lang);
        }
      }
    };
    load();
  }, [i18n]);

  // Load version info
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/version.json?t=${Date.now()}`)
      .then(r => r.json())
      .then(data => setVersionInfo(data))
      .catch(() => {});
  }, []);

  const toggleLanguage = () => {
    const newLang = currentLanguage === 'tr' ? 'en' : 'tr';
    i18n.changeLanguage(newLang);
    setCurrentLanguage(newLang);
    localStorage.setItem('chatbot_language', newLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await apiService.login(username, password);
      onLogin(result.user, result.token);
    } catch (err) {
      setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  const headerTitle = appSettings.headerTitle || t('login.title');
  const headerSubtitle = appSettings.headerSubtitle || '';

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    }}>
      {/* Header */}
      <div style={{
        padding: '24px',
        textAlign: 'center',
        color: 'white',
        position: 'relative',
      }}>
        {/* Language Toggle */}
        <button
          onClick={toggleLanguage}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '8px',
            padding: '6px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'white',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            border: 'none',
          }}
          title={t('language.select')}
        >
          <Globe size={14} />
          {currentLanguage === 'tr' ? 'TR' : 'EN'}
        </button>

        <div style={{
          width: '60px',
          height: '60px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <MessageCircle size={32} />
        </div>
        <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
          {headerTitle}
        </h1>
        {headerSubtitle && (
          <p style={{ fontSize: '13px', opacity: 0.85, margin: '4px 0 0' }}>
            {headerSubtitle}
          </p>
        )}
      </div>

      {/* Form */}
      <div style={{
        flex: 1,
        background: 'white',
        borderRadius: '24px 24px 0 0',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <form onSubmit={handleSubmit} style={{ marginBottom: '20px' }}>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '16px',
              color: '#dc2626',
              fontSize: '14px',
            }}>
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px',
            }}>
              {t('login.username')}
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: '#f3f4f6',
              borderRadius: '10px',
              padding: '0 12px',
            }}>
              <User size={18} color="#9ca3af" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('login.usernamePlaceholder')}
                required
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '14px',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px',
            }}>
              {t('login.password')}
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: '#f3f4f6',
              borderRadius: '10px',
              padding: '0 12px',
            }}>
              <Lock size={18} color="#9ca3af" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '14px',
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading
                ? '#9ca3af'
                : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <LogIn size={18} />
            {loading ? t('login.loading') : t('login.submit')}
          </button>
        </form>


        {/* Version Info */}
        {versionInfo && (
          <div style={{
            textAlign: 'center',
            padding: '8px 0 4px',
            fontSize: '10px',
            color: '#c0c0c0',
            letterSpacing: '0.5px'
          }}>
            v{versionInfo.version} | {versionInfo.buildDate}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
